import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import {
  KeyboardArrowDown,
  Settings,
  Save,
  Edit,
  Delete
} from '@mui/icons-material';
import { getCurrentView, getViews, getSetDefaultLoading, getViewManagementLoading, getCurrentViewAllDefaultFilteredLists } from '../ducks/selectors';
import {
  openViewDialog,
  setSelectedViewForEdit,
  setSelectedViews,
  setDefaultViewByViewData
} from '../ducks/actions';
import { DIALOG_SUBTYPES, DIALOG_TYPES, SYSTEM_DEFAULT_VIEW_NAME, SYSTEM_DEFAULT_VIEW_TYPE } from '../ducks/constants';
import styles from '../styles/viewManagement.module.scss';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';

interface ViewManagementButtonProps {
  className?: string;
}

//System default views are labelled "<client> - Portfolio Default View", with the client taken from the view data.
const getViewDisplayName = (view: any) => {
  if (view?.viewType !== SYSTEM_DEFAULT_VIEW_TYPE) {
    return view?.viewName;
  }
  const viewNamePrefix = (view?.viewName || '').includes(' - ') ? view.viewName.split(' - ')[0] : '';
  const clientName = view?.clientName || viewNamePrefix;
  return clientName ? `${clientName} - ${SYSTEM_DEFAULT_VIEW_NAME}` : SYSTEM_DEFAULT_VIEW_NAME;
};

     
const ViewManagementButton: React.FC<ViewManagementButtonProps> = ({
  className = '',
}) => {
  const dispatch = useDispatch();
  const currentView = useSelector(getCurrentView);
  const views = useSelector(getViews);
  const setDefaultLoading = useSelector(getSetDefaultLoading);
  const fetchViewsLoading = useSelector(getViewManagementLoading);
  const allDefaultFilteredLists = useSelector(getCurrentViewAllDefaultFilteredLists);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const isViewDropdownLoading = setDefaultLoading || fetchViewsLoading;

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isViewDropdownLoading) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewSelect = async (viewName: string, viewData: any) => {
    dispatch(setDefaultViewByViewData(viewData));
    handleClose();
  };

  const handleMenuAction = (action: string, dialogSubtype: string | null = null, viewData?: any) => {
    switch (action) {
      case 'rename':
        if (viewData) {
          dispatch(setSelectedViewForEdit(viewData));
        }
        break;
      case 'delete':
        if (viewData && !viewData.isDefault && !viewData.isSystem) {
          dispatch(setSelectedViews([viewData]));
        }
        break;
    }
    dispatch(openViewDialog(action, dialogSubtype as any, viewData));
    handleClose();
  };

  const getCurrentViewData = () => {
    return views.find((view: any) => view.viewName === currentView);
  };

  const getFilterStatusText = () => {
    const filteredSites = allDefaultFilteredLists?.sites || [];
    const filteredCostCenters = allDefaultFilteredLists?.costCenters || [];

    const siteText = filteredSites.length === 0 ? 'All sites' :
      filteredSites.length === 1 ? filteredSites[0].siteName : `${filteredSites.length} sites`;

    const costCenterText = filteredCostCenters.length === 0 ? 'all cost centers' :
      filteredCostCenters.length === 1 ? filteredCostCenters[0].description : `${filteredCostCenters.length} cost centers`;

    return `${siteText}, ${costCenterText}`;
  };

  const currentViewData = getCurrentViewData();
  const currentViewDisplayName = currentViewData ? getViewDisplayName(currentViewData) : currentView;
  const isSystemDefaultView = currentViewData?.viewType === SYSTEM_DEFAULT_VIEW_TYPE;
  const allViewNames = useMemo(() => {
    const getViewTypeOrder = (viewType?: string) => {
      if (viewType === SYSTEM_DEFAULT_VIEW_TYPE) return 0;
      if (viewType === 'custom') return 1;
      return 2;
    };

    return [...(views || [])].sort((a: any, b: any) => {
      const viewTypeOrderDiff = getViewTypeOrder(a?.viewType) - getViewTypeOrder(b?.viewType);

      if (viewTypeOrderDiff !== 0) {
        return viewTypeOrderDiff;
      }

      return (a?.viewName || '').localeCompare(b?.viewName || '', undefined, {
        sensitivity: 'base',
      });
    });
  }, [views]);
  const customViewCount = useMemo(
    () => (allViewNames || []).filter((view: any) => view?.viewType === 'custom').length,
    [allViewNames],
  );
  const nonCustomViewCount = useMemo(
    () => (allViewNames || []).filter((view: any) => view?.viewType !== 'custom').length,
    [allViewNames],
  );
  const shouldScrollViewList = customViewCount > 10;
  const visibleRowCount = nonCustomViewCount + Math.min(customViewCount, 10);
  const estimatedRowHeight = 40;
  const viewListMaxHeight = visibleRowCount * estimatedRowHeight;

  return (
    <Box className={`${styles.viewManagementContainer} ${className}`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleButtonClick}
          endIcon={isViewDropdownLoading ? <CircularProgress size={16} color="inherit" /> : <KeyboardArrowDown />}
          disabled={isViewDropdownLoading}
          className={`${styles.viewButton} view-management-button`}
          sx={{
            borderRadius: '4px',
            textTransform: 'none',
            color: '#666',
            // borderColor: 'transparent !important',
            border: 'none !important',
            backgroundColor: 'rgba(0, 63, 45, 0.08)',
            '&:hover': {
              // borderColor: 'transparent !important',
              border: 'none',
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          {setDefaultLoading ? 'Updating view...' : fetchViewsLoading ? 'Loading views...' : currentViewDisplayName}
        </Button>

        <Typography
          variant="body2"
          sx={{
            color: 'rgba(26, 26, 26, 0.65)',
            fontSize: '16px',
            whiteSpace: 'nowrap'
          }}
        >
          {getFilterStatusText()}
        </Typography>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={isViewDropdownLoading ? undefined : handleClose}
        PaperProps={{
          sx: {
            minWidth: '280px',
            mt: 1,
            border: '1px solid #e0e0e0',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >

        {allViewNames.length > 1 && <>
          <Box
            sx={{
              maxHeight: shouldScrollViewList ? viewListMaxHeight : 'unset',
              overflowY: shouldScrollViewList ? 'auto' : 'visible',
            }}
          >
            {allViewNames.map(({ viewName, ...rest }: any, index: number) => (
              <React.Fragment key={`${viewName}-${rest.portfolioViewId || index}`}>
                {index > 0 &&
                  allViewNames[index - 1]?.viewType === 'system_default' &&
                  rest?.viewType === 'custom' && <Divider sx={{ my: 0.5 }} />}
                <MenuItem
                  onClick={() => handleViewSelect(viewName, rest)}
                  selected={viewName === currentView}
                  disabled={isViewDropdownLoading}
                ><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <ListItemText primary={getViewDisplayName({ viewName, ...rest })} />
                    {viewName === currentView && (
                      <ListItemIcon>
                        <DoneOutlinedIcon fontSize="small" />
                      </ListItemIcon>
                    )}
                  </div>

                </MenuItem>
              </React.Fragment>
            ))
            }
          </Box>
          <Divider sx={{ my: 1 }} />
        </>}

        {/* View Management Actions */}
        <MenuItem onClick={() => handleMenuAction(DIALOG_TYPES.EDIT, DIALOG_SUBTYPES.EDIT_PRIMARY_LIST)} disabled={isViewDropdownLoading}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>

        <MenuItem onClick={() => handleMenuAction(DIALOG_TYPES.SAVE_AS)} disabled={isViewDropdownLoading}>
          <ListItemIcon>
            <Save fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Save view as..." />
        </MenuItem>

        <MenuItem
          onClick={() => handleMenuAction(DIALOG_TYPES.RENAME, currentViewData)}
          disabled={isViewDropdownLoading || isSystemDefaultView || !currentViewData}
        >
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Rename view"
            sx={{ color: isSystemDefaultView ? '#ccc' : 'inherit' }}
          />
        </MenuItem>

        <MenuItem
          onClick={() => handleMenuAction(DIALOG_TYPES.DELETE, currentViewData)}
          disabled={isViewDropdownLoading || isSystemDefaultView || !currentViewData}
          sx={{ color: '#A03530' }}
        >
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Delete view"
            sx={{
              color: '#A03530',
              '& span': { color: '#A03530' }
            }}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ViewManagementButton;